import { useEffect, useState } from "react";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";

import { applyTheme, DEFAULT_THEME, getActiveTheme, nextTheme } from "../../../utils/theme";
import type { ThemeName } from "../../../utils/theme";

/**
 * ============================================================================
 * ThemeToggle — flips <html data-theme> between dark and light (D-034).
 * ============================================================================
 *
 * The active value is READ FROM THE DOM, not computed. `index.html` has already
 * resolved cookie → prefers-color-scheme → dark and stamped the attribute
 * before first paint, so re-deriving it here could only disagree with what is
 * on screen.
 *
 * IT IS READ IN AN EFFECT, NOT IN A LAZY INITIALISER — changed in D-042, and
 * the reason is the SSG migration. This was
 * `useState<ThemeName>(() => getActiveTheme())`, which touches
 * `document.documentElement` DURING RENDER. That is fine in a browser and
 * throws in a server renderer, so it was one of exactly two things standing
 * between this app and static pre-rendering.
 *
 * The replacement is the standard two-pass pattern, and the choice matters:
 * server render and first client render must agree, or React discards the
 * server markup and re-renders the subtree — the "double render" the migration
 * is supposed to avoid. Both now start from DEFAULT_THEME, and the effect
 * corrects to the real value immediately after mount.
 *
 * WHAT THIS COSTS, stated plainly: on a machine set to light, the toggle's own
 * glyph shows the dark-theme affordance for one frame before the effect runs.
 * THE PAGE ITSELF DOES NOT FLASH — the boot script stamps `data-theme` before
 * first paint and every colour is a CSS token, so the theme is already correct
 * when the pixels land. Only this 36px control is briefly out of step, and only
 * on the first paint of a light-preferring visit. That is the irreducible cost
 * of putting client state in pre-rendered markup; the alternative (guessing the
 * theme during SSR) trades it for a genuine hydration mismatch.
 *
 * Tailwind rather than styled-components (Constitution V.5), and the colours
 * are tokens, so the control themes itself.
 */
const ThemeToggle = (): JSX.Element => {
	const [theme, setTheme] = useState<ThemeName>(DEFAULT_THEME);
	useEffect(() => {
		setTheme(getActiveTheme());
	}, []);
	const target = nextTheme(theme);

	const handleClick = (): void => {
		applyTheme(target);
		setTheme(target);
	};

	return (
		<button
			type="button"
			onClick={handleClick}
			/* The button announces the ACTION, not the state — "switch to light
			   theme" — because a screen-reader user cannot see which theme is
			   currently painted, and `aria-pressed` on a two-way switch reads
			   ambiguously ("pressed" meaning dark, or meaning light?). */
			aria-label={`Switch to ${target} theme`}
			title={`Switch to ${target} theme`}
			className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--tile-fill)] text-[var(--accent-interactive-text)] transition-colors duration-200 hover:bg-[var(--accent-interactive)] hover:text-[var(--accent-interactive-contrast)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent-interactive-text)]"
		>
			{theme === "dark" ? (
				<LightModeOutlinedIcon fontSize="small" aria-hidden="true" />
			) : (
				<DarkModeOutlinedIcon fontSize="small" aria-hidden="true" />
			)}
		</button>
	);
};

export default ThemeToggle;
